import { defineStore } from 'pinia';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  format,
  formatISO,
  isAfter,
  isBefore,
  isSameMinute,
} from 'date-fns';
import { Schedule } from '@/model/schedule';
import { Event } from '@/model/event';
import {
  apiDatesBetween,
  firstDayOfMonth,
  isDayWithin,
  monthGrid,
  selectedMonthOf,
  toDate,
  todayApiDate,
} from '@/helpers/date';
import employeeService from '@/services/employee.service';
import eventsService from '@/services/events.service';
import * as Sentry from '@sentry/vue';
import { Location } from '@/model/location';
import { API_DATE_FORMAT } from '@/constants';
import sortBy from 'lodash-es/sortBy';
import { CalendarEvent } from '@/components/calendar/models/calendar-event';
import cloneDeep from 'lodash-es/cloneDeep';
import i18n from '@/config/i18n';
import { reactive } from 'vue';
import { ComputedTimeRange } from '@/model/computed-time-range';
import type { CalendarSelectedMonth } from '@/model/calendar-selected-month';

const storeId = 'calendar';

function getResourceSchedules(
  dateFrom: string,
  dateTo: string,
  id: number,
): Schedule[] {
  const schedules: Schedule[] = [];
  let currentDate = toDate(dateFrom);
  const endDate = toDate(dateTo);
  do {
    schedules.push({
      available: true,
      date: format(currentDate, API_DATE_FORMAT),
      entityId: id,
      isException: false,
      summary: null,
      time_ranges: [
        {
          start: formatISO(currentDate),
          end: formatISO(endOfDay(currentDate)),
          location: {} as Location,
          own_category: null,
          possible_categories: [],
          position: 1,
        },
      ],
    });
    currentDate = addDays(currentDate, 1);
  } while (!isAfter(currentDate, endDate));
  return schedules;
}

export enum CalendarResourceType {
  EMPLOYEE = 'EMPLOYEE',
  MACHINE = 'MACHINE',
  WORKPLACE = 'WORKPLACE',
}

export enum CalendarViewMode {
  PERSONAL = 'PERSONAL',
  TEAM = 'TEAM',
}

export enum CalendarDisplayType {
  PLANNING = 'PLANNING',
  DAY_GRID = 'DAY_GRID',
  WEEK_GRID = 'WEEK_GRID',
}

export interface CalendarSchedulesMap {
  [id: number]: {
    [date: string]: Schedule;
  };
}

export interface CalendarEventsMap {
  [id: number]: {
    [date: string]: Event[];
  };
}

let initialState = reactive({
  viewType: CalendarResourceType.EMPLOYEE,
  viewMode: CalendarViewMode.PERSONAL,
  displayType: CalendarDisplayType.DAY_GRID,
  previousDisplayType: null as CalendarDisplayType | null,
  forceShowSchedule: false as boolean,
  reduced: false as boolean,
  selectedMonth: selectedMonthOf(new Date()),
  selectedDate: todayApiDate(),
  startOfDay: 8,
  zoom: 0 as number,
  schedules: {
    fetching: false as boolean,
    failure: false as boolean,
    schedules: {
      [CalendarResourceType.EMPLOYEE]: {} as CalendarSchedulesMap,
      [CalendarResourceType.MACHINE]: {} as CalendarSchedulesMap,
      [CalendarResourceType.WORKPLACE]: {} as CalendarSchedulesMap,
    },
  },
  events: {
    fetching: false as boolean,
    failure: false as boolean,
    events: {
      [CalendarResourceType.EMPLOYEE]: {} as CalendarEventsMap,
      [CalendarResourceType.MACHINE]: {} as CalendarEventsMap,
      [CalendarResourceType.WORKPLACE]: {} as CalendarEventsMap,
    },
  },
});
const initialStateClone = cloneDeep(initialState);
if (localStorage.getItem(storeId)) {
  initialState = reactive({
    ...initialState,
    ...JSON.parse(localStorage.getItem(storeId)),
  });
  initialState.selectedMonth = selectedMonthOf(initialState.selectedDate);
}

export const useCalendarStore = defineStore(storeId, {
  state: () => {
    return {
      ...initialState,
    };
  },
  getters: {
    getActualTimeRange:
      (state) =>
      (
        entityId: number,
        dateString: string,
        timeString: string,
      ): ComputedTimeRange | null => {
        return state.schedules.schedules[state.viewType]?.[entityId]?.[
          dateString
        ]?.time_ranges?.filter((timeRange) => {
          const at = toDate(`${dateString} ${timeString}`);

          return (
            isAfter(at, toDate(`${dateString} ${timeRange.start}`)) &&
            isBefore(at, toDate(`${dateString} ${timeRange.end}`))
          );
        })[0];
      },
    getSchedule:
      (state) =>
      (entityId: number, dateString: string): Schedule | null => {
        try {
          return (
            state.schedules.schedules[state.viewType]?.[entityId]?.[
              dateString
            ] ?? null
          );
        } catch (e) {
          Sentry.captureException(e);
        }
      },
    getEvents:
      (state) =>
      (entityId: number, dateString: string): Event[] | [] => {
        try {
          return (
            state.events.events[state.viewType]?.[entityId]?.[dateString] ?? []
          );
        } catch (e) {
          Sentry.captureException(e);
        }
      },
  },
  actions: {
    getCalendarEvents(entityId: number, date: string): CalendarEvent[] {
      const events: Event[] =
        this.events.events[this.viewType]?.[entityId]?.[date] ?? [];
      let calendarEvents: CalendarEvent[] = [];
      const eventsStartsToSkip: string[] = [];

      events.forEach((event: Event) => {
        const calEvent: CalendarEvent = {
          start: event.start,
          end: event.end,
          entity_id: entityId,
          event_id: event.id,
          summary: event.summary,
          customer: event.booking?.customer ?? null,
          workplace_id: event.workplace_id,
          location_id: event.location_id,
          comment: event.booking?.comment ?? null,
          private_comment: event.booking?.private_comment ?? null,
          status: event.booking?.status ?? null,
          services_ids: event.booking ? [event.service_id] : null,
          services_names: event.booking
            ? [
                event.service.article._translations[i18n.global.locale.value]
                  .name,
              ]
            : null,
          booking_id: event.booking ? event.booking_id : null,
          invoice: event.booking?.invoice ?? null,
        };
        if (eventsStartsToSkip.includes(calEvent.start)) {
          return;
        }
        if (calEvent.booking_id) {
          const eventsOfBookingWithSameWorkplace: Event[] = events.filter(
            (loopEvent) => {
              return (
                loopEvent.booking_id === calEvent.booking_id &&
                loopEvent.workplace_id === calEvent.workplace_id &&
                loopEvent.service_part_id === null
              );
            },
          );
          if (eventsOfBookingWithSameWorkplace.length > 1) {
            const servicesIds: number[] = [];
            const servicesNames: string[] = [];
            eventsOfBookingWithSameWorkplace.forEach((event) => {
              servicesIds.push(event.service_id);
              servicesNames.push(
                event.service.article._translations[i18n.global.locale.value]
                  .name,
              );
              eventsStartsToSkip.push(event.start);
            });
            calendarEvents.push({
              start: eventsOfBookingWithSameWorkplace[0].start,
              end: eventsOfBookingWithSameWorkplace[
                eventsOfBookingWithSameWorkplace.length - 1
              ].end,
              entity_id: entityId,
              workplace_id: eventsOfBookingWithSameWorkplace[0].workplace_id,
              location_id: eventsOfBookingWithSameWorkplace[0].location_id,
              booking_id: eventsOfBookingWithSameWorkplace[0].booking_id,
              customer: eventsOfBookingWithSameWorkplace[0].booking.customer,
              comment: eventsOfBookingWithSameWorkplace[0].booking.comment,
              private_comment:
                eventsOfBookingWithSameWorkplace[0].booking.private_comment,
              status:
                eventsOfBookingWithSameWorkplace[0].booking?.status ?? null,
              services_ids: servicesIds,
              services_names: servicesNames,
              invoice: event.booking?.invoice ?? null,
            });
          } else {
            calendarEvents.push(calEvent);
          }
        } else {
          calendarEvents.push(calEvent);
        }
      });

      calendarEvents = calendarEvents.filter((item) => {
        return !isSameMinute(toDate(item.start), toDate(item.end));
      });
      calendarEvents = calendarEvents.sort(function compare(itemA, itemB) {
        return isAfter(toDate(itemA.start), toDate(itemB.start)) ? 1 : -1;
      });

      return calendarEvents;
    },
    setViewMode(viewMode: CalendarViewMode) {
      this.viewMode = viewMode;
    },
    setViewType(viewType: CalendarResourceType) {
      this.viewType = viewType;
    },
    setDisplayType(displayType: CalendarDisplayType) {
      this.previousDisplayType =
        this.displayType !== CalendarDisplayType.WEEK_GRID
          ? this.displayType
          : null;
      this.displayType = displayType;
    },
    changeDayOrWeek(increment: number, unit: 'day' | 'week') {
      const selectedDate = toDate(this.selectedDate);
      const nextDate =
        unit === 'week'
          ? addWeeks(selectedDate, increment)
          : addDays(selectedDate, increment);
      if (selectedDate.getMonth() !== nextDate.getMonth()) {
        this.changeMonth(increment);
      }
      this.selectDate(format(nextDate, API_DATE_FORMAT));
      const nextMonth = selectedMonthOf(nextDate);
      if (
        this.selectedMonth.monthIndex !== nextMonth.monthIndex ||
        this.selectedMonth.year !== nextMonth.year
      ) {
        this.setMonthYear(nextMonth);
      }
    },
    changeMonth(increment: number) {
      this.setMonthYear(
        selectedMonthOf(
          addMonths(firstDayOfMonth(this.selectedMonth), increment),
        ),
      );
    },
    setStartOfDay(startOfDay: number) {
      this.startOfDay = startOfDay;
    },
    setZoom(zoomLevel: number) {
      this.zoom = zoomLevel;
    },
    selectDate(date: string) {
      this.$patch({
        selectedDate: date,
      });
    },
    setMonthYear(month: CalendarSelectedMonth) {
      this.selectedMonth = month;
    },
    addEvent(event: Event, date: string, entityId: string | number) {
      const days = this.events.events[this.viewType][entityId] ?? {};
      const otherEvents: Event[] = (days[date] ?? []).filter(
        (existingEvent: Event) => existingEvent.id !== event.id,
      );
      this.events.events[this.viewType] = {
        ...this.events.events[this.viewType],
        [entityId]: {
          ...days,
          [date]: sortBy([...otherEvents, event], ['start']),
        },
      };
    },
    deleteEvent(eventId: number) {
      Object.values(this.events.events).forEach((entities) => {
        Object.values(entities).forEach((days) => {
          Object.keys(days).forEach((date) => {
            days[date] = days[date].filter(
              (loopEvent: Event) => loopEvent.id !== eventId,
            );
          });
        });
      });
    },
    async findSchedulesBetweenDates(payload: {
      employeeId?: number;
      workplaceId?: number;
      machineId?: number;
      dateFrom: string;
      dateTo: string;
      resourcesIds?: number[];
      quietly?: boolean;
    }): Promise<void> {
      if (this.viewType !== CalendarResourceType.EMPLOYEE) {
        let schedules: Schedule[] = [];
        if (payload.resourcesIds) {
          for (const resourceId of payload.resourcesIds) {
            schedules = [
              ...schedules,
              ...getResourceSchedules(
                payload.dateFrom,
                payload.dateTo,
                resourceId,
              ),
            ];
          }
        } else {
          schedules = getResourceSchedules(
            payload.dateFrom,
            payload.dateTo,
            (payload.workplaceId as number) ?? (payload.machineId as number),
          );
        }
        this._setSchedules(schedules);
        return;
      }
      try {
        if (!payload.quietly) {
          this.schedules.fetching = true;
        }
        this.schedules.failure = false;
        const selectedMonthGrid = monthGrid(toDate(this.selectedDate));
        const schedules = (
          await employeeService.getSchedulesBetweenDates(
            payload.dateFrom,
            payload.dateTo,
            payload.employeeId,
          )
        ).map((schedule) => ({ ...schedule, entityId: schedule.employee_id }));
        this._setSchedules(schedules);

        const entityId =
          payload.employeeId ?? payload.workplaceId ?? payload.machineId;
        if (
          !isDayWithin(this.selectedDate, payload.dateFrom, payload.dateTo) &&
          !this.schedules.schedules[this.viewType]?.[entityId]?.[
            this.selectedDate
          ]
        ) {
          const schedules = (
            await employeeService.getSchedulesBetweenDates(
              format(selectedMonthGrid.start, API_DATE_FORMAT),
              format(selectedMonthGrid.end, API_DATE_FORMAT),
              payload.employeeId,
            )
          ).map((schedule) => ({
            ...schedule,
            entityId: schedule.employee_id,
          }));
          this._setSchedules(schedules);
        }
      } catch {
        this.schedules.fetching = false;
        this.schedules.failure = true;
      }
    },
    _setSchedules(schedules: Schedule[]) {
      schedules.forEach((schedule) => {
        if (
          typeof this.schedules.schedules[this.viewType][
            schedule.entityId as number
          ] === 'undefined'
        ) {
          this.schedules.schedules[this.viewType] = Object.assign(
            {},
            this.schedules.schedules[this.viewType],
            { [schedule.entityId]: {} },
          );
        }
        this.schedules.schedules[this.viewType][schedule.entityId as number] =
          Object.assign(
            {},
            this.schedules.schedules[this.viewType][
              schedule.entityId as number
            ],
            { [schedule.date]: schedule },
          );
      });
      this.$patch({
        schedules: {
          ...this.schedules,
          fetching: false,
        },
      });
    },
    async findEventsBetweenDates(payload: {
      employeeId?: number;
      workplaceId?: number;
      machineId?: number;
      dateFrom: string;
      dateTo: string;
      quietly?: boolean;
    }) {
      try {
        if (!payload.quietly) {
          this.events.fetching = true;
        }
        this.events.failure = false;
        let events = await eventsService.findBetweenDates(
          payload.dateFrom,
          payload.dateTo,
          payload.employeeId,
          payload.machineId,
          payload.workplaceId,
        );
        const dates = apiDatesBetween(payload.dateFrom, payload.dateTo);

        if (!isDayWithin(this.selectedDate, payload.dateFrom, payload.dateTo)) {
          const { start, end } = monthGrid(toDate(this.selectedDate));
          const eventsForMonth = await eventsService.findBetweenDates(
            format(start, API_DATE_FORMAT),
            format(end, API_DATE_FORMAT),
            payload.employeeId,
            payload.machineId,
            payload.workplaceId,
          );

          dates.push(...apiDatesBetween(start, end));

          events = events.concat(eventsForMonth);
        }
        this._setEvents(events, dates);
      } catch (exception) {
        console.error(exception);
        Sentry.captureException(exception);
        this.events.failure = true;
        this.events.fetching = false;
      }
    },
    _setEvents(actualEvents: Event[], dates: Array<string>) {
      const viewType: CalendarResourceType = this.viewType;
      const events = cloneDeep(this.events);
      Object.keys(events.events[viewType]).forEach((entityId) => {
        Object.keys(events.events[viewType][entityId]).forEach((dateKey) => {
          if (dates.includes(dateKey)) {
            delete events.events[viewType][entityId][dateKey];
          }
        });
      });
      actualEvents.forEach((event) => {
        const entityId: number =
          viewType === CalendarResourceType.WORKPLACE
            ? (event.workplace_id as number)
            : viewType === CalendarResourceType.MACHINE
              ? (event.machine_id as number)
              : (event.employee_id as number);
        const dateKey = format(toDate(event.start), API_DATE_FORMAT);
        if (typeof events.events[viewType][entityId] === 'undefined') {
          events.events[viewType][entityId] = {};
        }
        if (typeof events.events[viewType][entityId][dateKey] === 'undefined') {
          events.events[viewType][entityId] = {
            ...events.events[viewType][entityId],
            [dateKey]: [],
          };
        }
        const existingEventIndex = events.events[viewType][entityId][
          dateKey
        ].findIndex((ev) => ev.id === event.id);
        if (existingEventIndex >= 0) {
          events.events[viewType][entityId][dateKey][existingEventIndex] =
            event;
        } else {
          events.events[viewType][entityId][dateKey].push(event);
        }
      });
      this.events = Object.assign({}, events);
      this.$patch({
        events: {
          ...events,
          fetching: false,
        },
      });
    },
    reset() {
      this.$state = Object.assign(
        {},
        this.$state,
        cloneDeep(initialStateClone),
      );
    },
  },
});
